import { memo, useMemo } from "react";
import { useLocation, Link } from "react-router-dom";

// Interface defining the structure of each breadcrumb link
interface BreadcrumbItem {
  label: string;
  to: string;
}

// Maps technical path names (from URLs) to friendly UI display names
const PATH_LABEL_MAP: Record<string, string> = {
  dashboard: "Dashboard",
  jobs: "Your Jobs",
  candidates: "Shortlisted Candidates",
  interviews: "Interviews",
  help: "Help Center",
  create: "Create Job",
  edit: "Edit Job",
};

// Formats a URL segment into a clean label (e.g. database hashes -> "Details", others -> friendly names / capitalized fallback)
const getBreadcrumbLabel = (segment: string) =>
  segment.length >= 24
    ? "Details"
    : PATH_LABEL_MAP[segment] ||
      segment.charAt(0).toUpperCase() + segment.slice(1);

// Generates the array of breadcrumbs from a URL path in a single loop
const getBreadcrumbItems = (pathname: string): BreadcrumbItem[] => {
  const segments = pathname.split("/").filter(Boolean);
  const items: BreadcrumbItem[] = [];
  let path = "";

  segments.forEach((seg, i) => {
    path += `/${seg}`;
    if (i === 0) {
      // If we start at dashboard, show "Dashboard". Otherwise, prepend "Home" -> Dashboard.
      items.push(
        seg === "dashboard"
          ? { label: "Dashboard", to: "/dashboard" }
          : { label: "Home", to: "/dashboard" },
      );
      // Skip pushing dashboard twice when it is the first segment
      if (seg === "dashboard") return;
    }
    items.push({ label: getBreadcrumbLabel(seg), to: path });
  });

  return items;
};

export default memo(function Breadcrumb() {
  const { pathname } = useLocation();

  // Re-generate breadcrumb array only when the pathname changes
  const items = useMemo(() => getBreadcrumbItems(pathname), [pathname]);

  return (
    <nav className="flex items-center gap-1.5 text-brand-black-2 font-medium">
      {items.map((item, i) => (
        <div key={item.to} className="flex items-center gap-1.5">
          {/* Display a "/" separator before all items except the first one */}
          {i > 0 && <span className="text-brand-black-2/30 text-xs">/</span>}

          {/* Current page (last index) is colored and unclickable; other items are navigation links */}
          {i === items.length - 1 ? (
            <span className="text-brand-dark-orange font-semibold">
              {item.label}
            </span>
          ) : (
            <Link
              to={item.to}
              className="hover:text-brand-dark-orange transition-colors"
            >
              {item.label}
            </Link>
          )}
        </div>
      ))}
    </nav>
  );
});
